import {
  Button,
  Datepicker,
  InnerContent,
  SimpleModal,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalTitle,
} from '@fountain/fountain-ui-components';
import { Grid, Typography } from '@material-ui/core';
import { subDays } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import { makeSelectLocale } from 'containers/LanguageProvider/selectors';
import { usePostHog } from 'hooks/usePostHog';
import globalMessages from 'shared/global/messages';
import { getDateFormatPattern } from 'utils/datePlaceholder';

import { exportAgentReport, getAgentReportConversationCount } from '../../api';
import { messages } from '../../messages';
import { AxAgentResponse } from '../../types';
import {
  getReportDateRangeError,
  ReportDateRangeErrorType,
  toLocalDateISOString,
} from '../../utils';
import { useExportReportModalStyles } from '../styles';

const CONVERSATION_COUNT_DEBOUNCE_MS = 500;

const dateRangeErrorMessages: Record<
  ReportDateRangeErrorType,
  (typeof messages)[keyof typeof messages]
> = {
  range: messages.exportReportDateRangeError,
  future: messages.exportReportFutureDateError,
  tooWide: messages.exportReportRangeTooWideError,
};

interface ExportAgentReportModalProps {
  onClose: () => void;
  agent?: AxAgentResponse;
  accountSlug: string;
}

export const ExportAgentReportModal: React.FC<ExportAgentReportModalProps> = ({
  onClose,
  agent,
  accountSlug,
}) => {
  const classes = useExportReportModalStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const posthog = usePostHog();
  const locale = useSelector(makeSelectLocale());
  const whoami = useSelector(makeSelectWhoami());

  // Default the range to the last 30 days.
  const [startDate, setStartDate] = useState<Date | undefined>(() =>
    subDays(new Date(), 30),
  );
  const [endDate, setEndDate] = useState<Date | undefined>(() => new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conversationCount, setConversationCount] = useState<number>();
  const [meetsMinimum, setMeetsMinimum] = useState<boolean>();

  const today = new Date();
  const dateRangeError = getReportDateRangeError(startDate, endDate);
  const startTime = startDate?.getTime();
  const endTime = endDate?.getTime();

  // Approximate pre-flight conversation count, debounced so it only fires once
  // the user settles on a range rather than on every calendar click. Fails
  // open on error or while pending -- only a confirmed low-volume result
  // blocks submission, so the CTA isn't disabled by default on every open.
  useEffect(() => {
    setConversationCount(undefined);
    setMeetsMinimum(undefined);

    if (dateRangeError || !agent?.uuid || !startDate || !endDate) {
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      void getAgentReportConversationCount(accountSlug, agent.uuid, {
        startDate: toLocalDateISOString(startDate),
        endDate: toLocalDateISOString(endDate),
      })
        .then(response => {
          if (cancelled) return;
          setConversationCount(response.conversation_count);
          setMeetsMinimum(response.meets_minimum);
        })
        .catch(() => {
          // Fail open: don't block submission when the pre-flight check itself fails.
        });
    }, CONVERSATION_COUNT_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountSlug, agent?.uuid, startTime, endTime, dateRangeError]);

  // Non-blocking: ConversationCountQuery is a documented lower bound of the
  // Hex/BigQuery population (Postgres AgentThread rows only), so a count below
  // the minimum doesn't prove Hex's real dataset is too thin -- it only warns
  // the recruiter before they wait on a run that may come back sparse.
  const hasLowVolume = !dateRangeError && meetsMinimum === false;
  const canSubmit =
    !dateRangeError && !!startDate && !!endDate && !isSubmitting;

  const handleCancel = () => {
    posthog.capture('candidate_ai_agent:export_report_cancel', {
      agent_id: agent?.uuid,
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (!agent?.uuid || !startDate || !endDate) return;

    setIsSubmitting(true);
    try {
      await exportAgentReport(accountSlug, agent.uuid, {
        startDate: toLocalDateISOString(startDate),
        endDate: toLocalDateISOString(endDate),
      });
      posthog.capture('candidate_ai_agent:export_report_submit', {
        agent_id: agent.uuid,
      });
      // The ticket's AC names this event `cai_analysis_report_requested`, but
      // recruiter-ui's analytics catalog (utils/analytics.ts) enforces a
      // `category:object_action` naming convention with a fixed action list --
      // a bare `_requested` suffix doesn't type-check. Reusing the existing
      // candidate_ai_agent:export_report category/object with the closest
      // registered action instead of adding a new one-off action to the shared list.
      posthog.capture('candidate_ai_agent:export_report_generate', {
        agent_id: agent.uuid,
        range_start: toLocalDateISOString(startDate).slice(0, 10),
        range_end: toLocalDateISOString(endDate).slice(0, 10),
        conversation_count: conversationCount,
      });
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.exportReportRequestSuccess, {
            userEmail: whoami.email,
          }),
          'success',
        ),
      );
      onClose();
    } catch (err) {
      // Surface the underlying failure (status/body) instead of swallowing it —
      // the generic flash alone makes report failures impossible to diagnose.
      // eslint-disable-next-line no-console
      console.error('Failed to trigger conversation report export', err);
      posthog.capture('candidate_ai_agent:export_report_fail', {
        agent_id: agent.uuid,
        error: err instanceof Error ? err.message : String(err),
      });
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.exportReportRequestFailed),
          'error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SimpleModal
      onClose={handleCancel}
      maxWidth={480}
      blurOverlay={false}
      ariaLabelledBy="export-report-modal-title"
    >
      <SimpleModalHeader onClose={handleCancel}>
        <SimpleModalTitle id="export-report-modal-title">
          <FormattedMessage {...messages.exportReportModalTitle} />
        </SimpleModalTitle>
      </SimpleModalHeader>
      <InnerContent>
        <Typography variant="body2" className={classes.description}>
          <FormattedMessage
            {...messages.exportReportModalDescription}
            values={{ agentName: agent?.name }}
          />
        </Typography>
        <Typography variant="caption" className={classes.description}>
          <FormattedMessage
            {...messages.exportReportHelperText}
            values={{ userEmail: whoami.email }}
          />
        </Typography>
        <Grid container className={classes.dateRangeContainer}>
          <div className={classes.dateField}>
            <Typography
              id="export-report-start-date-label"
              variant="body2"
              className={classes.dateLabel}
            >
              <FormattedMessage {...messages.startDate} />
            </Typography>
            <Datepicker
              ariaLabelledBy="export-report-start-date-label"
              maxDate={endDate ?? today}
              locale={locale}
              value={startDate}
              onDateChange={date => setStartDate(date as Date | undefined)}
              renderDate={selectedDate => (
                <FormattedDate dateStyle="short" value={selectedDate} />
              )}
              renderPlaceholder={() => getDateFormatPattern(locale)}
            />
          </div>
          <div className={classes.dateField}>
            <Typography
              id="export-report-end-date-label"
              variant="body2"
              className={classes.dateLabel}
            >
              <FormattedMessage {...messages.endDate} />
            </Typography>
            <Datepicker
              ariaLabelledBy="export-report-end-date-label"
              minDate={startDate}
              maxDate={today}
              locale={locale}
              value={endDate}
              onDateChange={date => setEndDate(date as Date | undefined)}
              renderDate={selectedDate => (
                <FormattedDate dateStyle="short" value={selectedDate} />
              )}
              renderPlaceholder={() => getDateFormatPattern(locale)}
            />
          </div>
        </Grid>
        {dateRangeError && (
          <Typography
            variant="caption"
            color="error"
            className={classes.dateRangeError}
          >
            <FormattedMessage {...dateRangeErrorMessages[dateRangeError]} />
          </Typography>
        )}
        {hasLowVolume && (
          <Typography
            variant="caption"
            color="error"
            className={classes.dateRangeError}
          >
            <FormattedMessage {...messages.exportReportLowVolumeError} />
          </Typography>
        )}
      </InnerContent>
      <SimpleModalFooter>
        <Button
          type="secondary"
          onClick={handleCancel}
          className={classes.cancelButton}
        >
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        <Button
          submit
          type="primary"
          isLoading={isSubmitting}
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          <FormattedMessage {...messages.emailReport} />
        </Button>
      </SimpleModalFooter>
    </SimpleModal>
  );
};
